import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Plus, Pencil, Trash2, Loader2, Users, Tag, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function InterestsPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', color: '#2db8b0', icon: 'tag' });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Contacts for a category
  const [selectedCat, setSelectedCat] = useState(null);
  const [catContacts, setCatContacts] = useState([]);
  const [catLoading, setCatLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const r = await api.get('/interest-categories');
      setCategories(r.data.items);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => { setForm({ name: '', description: '', color: '#2db8b0', icon: 'tag' }); setEditId(null); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, description: c.description || '', color: c.color || '#2db8b0', icon: c.icon || 'tag' }); setEditId(c.id); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) { await api.put(`/interest-categories/${editId}`, form); toast.success('Kategoria zaktualizowana'); }
      else { await api.post('/interest-categories', form); toast.success('Kategoria dodana'); }
      setDialogOpen(false); fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac kategorie? Zainteresowania zostana usuniete z kontaktow.')) return;
    try { await api.delete(`/interest-categories/${id}`); toast.success('Usunieto'); fetchCategories(); if (selectedCat?.id === id) setSelectedCat(null); }
    catch { toast.error('Blad'); }
  };

  const viewCatContacts = async (cat) => {
    setSelectedCat(cat);
    setCatLoading(true);
    try {
      const r = await api.get(`/contacts/by-interest/${cat.slug}`);
      setCatContacts(r.data.items);
    } catch {} finally { setCatLoading(false); }
  };

  const COLORS = ['#2db8b0', '#f59e0b', '#8b5cf6', '#3b82f6', '#10b981', '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#0ea5e9', '#22c55e', '#f97316'];

  return (
    <div className="space-y-6" data-testid="interests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Kategorie zainteresowań</h1>
          <p className="text-sm text-muted-foreground mt-1">Segmentuj kontakty wg zainteresowań dla skuteczniejszego mailingu</p>
        </div>
        <Button onClick={openCreate} data-testid="interests-add-button"><Plus className="h-4 w-4 mr-2" />Nowa kategoria</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map(cat => (
            <Card key={cat.id} className={`border-border/50 bg-card/80 cursor-pointer hover:shadow-lg transition-shadow duration-200 ${selectedCat?.id === cat.id ? 'ring-2 ring-primary/50' : ''}`}
              onClick={() => viewCatContacts(cat)}>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${cat.color}15` }}>
                    <Tag className="h-5 w-5" style={{ color: cat.color }} />
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); openEdit(cat); }}><Pencil className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(cat.id); }}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
                <h3 className="font-medium text-sm">{cat.name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{cat.description}</p>
                <div className="flex items-center gap-2 mt-3">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-mono text-muted-foreground" data-testid={`cat-count-${cat.slug}`}>{cat.contact_count || 0} kontaktow</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Selected category contacts */}
      {selectedCat && (
        <Card className="border-border/50 bg-card/80">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${selectedCat.color}15` }}>
                <Tag className="h-4 w-4" style={{ color: selectedCat.color }} />
              </div>
              <div>
                <CardTitle className="font-heading text-base">Kontakty: {selectedCat.name}</CardTitle>
                <p className="text-xs text-muted-foreground">{catContacts.length} kontaktow w tej kategorii</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {catLoading ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10" />)}</div>
            ) : catContacts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Brak kontaktow w tej kategorii. Przypisz zainteresowania w zakladce Kontakty.</p>
            ) : (
              <div className="space-y-1">
                {catContacts.slice(0, 20).map(c => (
                  <div key={c.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-muted/30 transition-colors duration-150">
                    <span className="text-sm font-medium w-40 truncate">{c.first_name} {c.last_name}</span>
                    <span className="text-xs font-mono text-muted-foreground w-32">{c.phone || '-'}</span>
                    <span className="text-xs text-muted-foreground flex-1 truncate">{c.email || '-'}</span>
                    <span className="text-xs text-muted-foreground">{c.city || ''}</span>
                    <Badge variant="outline" className="text-xs font-mono">{c.activity_score} pkt</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? 'Edytuj kategorie' : 'Nowa kategoria zainteresowań'}</DialogTitle>
            <DialogDescription>Kategorie sluza do segmentacji kontaktow</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nazwa kategorii</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="np. Pozyczki / Kredyty" data-testid="interest-form-name" />
            </div>
            <div className="space-y-2">
              <Label>Opis</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Krotki opis kategorii" data-testid="interest-form-description" />
            </div>
            <div className="space-y-2">
              <Label>Kolor</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map(c => (
                  <button key={c} onClick={() => setForm({...form, color: c})}
                    className={`w-8 h-8 rounded-lg transition-transform duration-150 ${form.color === c ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110' : 'hover:scale-105'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={saving || !form.name} data-testid="interest-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
